import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import Input from './Input';

const ProviderSidebar = ({ 
  isCollapsed = false,
  patients = [],
  selectedPatientId = null,
  onPatientSelect = () => {},
  className = ""
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Mock patient data if none provided
  const mockPatients = [
    { id: 1, name: 'Sarah Johnson', status: 'active', lastVisit: '2024-08-22', riskLevel: 'low' },
    { id: 2, name: 'Michael Chen', status: 'active', lastVisit: '2024-08-21', riskLevel: 'medium' },
    { id: 3, name: 'Emma Rodriguez', status: 'inactive', lastVisit: '2024-08-18', riskLevel: 'high' },
    { id: 4, name: 'David Thompson', status: 'active', lastVisit: '2024-08-20', riskLevel: 'low' },
    { id: 5, name: 'Lisa Wang', status: 'active', lastVisit: '2024-08-23', riskLevel: 'medium' }
  ];

  const patientList = patients?.length > 0 ? patients : mockPatients;

  const filteredPatients = patientList?.filter(patient =>
    patient?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  const handlePatientClick = (patient) => {
    onPatientSelect(patient);
    navigate('/detailed-patient-view', { state: { patientId: patient?.id } });
    setIsMobileOpen(false);
  };

  const handleDashboardClick = () => {
    navigate('/provider-dashboard');
    setIsMobileOpen(false);
  };

  const getRiskLevelColor = (level) => {
    switch (level) {
      case 'high': return 'text-error';
      case 'medium': return 'text-warning';
      case 'low': return 'text-success';
      default: return 'text-muted-foreground';
    }
  };

  const getRiskLevelBg = (level) => {
    switch (level) {
      case 'high': return 'bg-error/10';
      case 'medium': return 'bg-warning/10';
      case 'low': return 'bg-success/10';
      default: return 'bg-muted';
    }
  };

  // Desktop Sidebar
  const DesktopSidebar = () => (
    <aside className={`
      hidden lg:flex lg:fixed lg:left-0 lg:top-16 lg:bottom-0 lg:z-[900]
      ${isCollapsed ? 'lg:w-16' : 'lg:w-80'}
      bg-card border-r border-border transition-all duration-300
    `}>
      <div className="flex flex-col w-full">
        {/* Header */}
        <div className="p-4 border-b border-border">
          <Button
            variant={location?.pathname === '/provider-dashboard' ? 'default' : 'ghost'}
            onClick={handleDashboardClick}
            className="w-full justify-start"
          >
            <Icon name="LayoutDashboard" size={18} />
            {!isCollapsed && <span className="ml-2">Provider Dashboard</span>}
          </Button>
        </div>

        {!isCollapsed && (
          <>
            {/* Search */}
            <div className="p-4 border-b border-border">
              <Input
                type="search"
                placeholder="Search patients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full"
              />
            </div>

            {/* Patient List */}
            <div className="flex-1 overflow-y-auto">
              <div className="p-2">
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide px-2 py-2">
                  Patients ({filteredPatients?.length})
                </div>
                
                <div className="space-y-1">
                  {filteredPatients?.map((patient) => (
                    <button
                      key={patient?.id}
                      onClick={() => handlePatientClick(patient)}
                      className={`
                        w-full p-3 rounded-lg text-left transition-colors duration-150
                        ${selectedPatientId === patient?.id 
                          ? 'bg-primary text-primary-foreground' 
                          : 'hover:bg-muted text-foreground'
                        }
                      `}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{patient?.name}</div>
                          <div className="text-xs opacity-75 mt-1">
                            Last visit: {patient?.lastVisit}
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2 ml-2">
                          <div className={`
                            w-2 h-2 rounded-full
                            ${patient?.status === 'active' ? 'bg-success' : 'bg-muted-foreground'}
                          `} />
                          <div className={`
                            px-2 py-1 rounded text-xs font-medium
                            ${getRiskLevelBg(patient?.riskLevel)} ${getRiskLevelColor(patient?.riskLevel)}
                          `}>
                            {patient?.riskLevel}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="p-4 border-t border-border">
              <div className="space-y-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => {/* Handle AI analysis */}}
                >
                  <Icon name="Brain" size={16} />
                  <span className="ml-2">Generate AI Analysis</span>
                </Button>
                
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => {/* Handle export */}}
                >
                  <Icon name="Download" size={16} />
                  <span className="ml-2">Export Reports</span>
                </Button>
              </div>
            </div>
          </>
        )}

        {isCollapsed && (
          <div className="flex-1 p-2">
            <div className="space-y-2">
              {filteredPatients?.slice(0, 5)?.map((patient) => (
                <button
                  key={patient?.id}
                  onClick={() => handlePatientClick(patient)}
                  className={`
                    w-12 h-12 rounded-lg flex items-center justify-center transition-colors duration-150
                    ${selectedPatientId === patient?.id 
                      ? 'bg-primary text-primary-foreground' 
                      : 'hover:bg-muted text-foreground'
                    }
                  `}
                  title={patient?.name}
                >
                  <Icon name="User" size={18} />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </aside>
  );

  // Mobile Sidebar
  const MobileSidebar = () => (
    <div className="lg:hidden">
      {/* Mobile Toggle Button */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsMobileOpen(true)}
        className="fixed bottom-4 right-4 z-[950] shadow-soft-lg"
      >
        <Icon name="Users" size={18} />
        <span className="ml-2">Patients</span>
      </Button>

      {/* Mobile Sidebar Overlay */}
      {isMobileOpen && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 z-[900]" 
            onClick={() => setIsMobileOpen(false)}
          />
          <div className="fixed left-0 top-16 bottom-0 w-80 bg-card border-r border-border z-[950] animate-slide-in">
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="p-4 border-b border-border flex items-center justify-between">
                <h3 className="font-semibold text-foreground">Patients</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsMobileOpen(false)}
                >
                  <Icon name="X" size={18} />
                </Button>
              </div>

              {/* Search */}
              <div className="p-4 border-b border-border">
                <Input
                  type="search"
                  placeholder="Search patients..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                />
              </div>

              {/* Patient List */}
              <div className="flex-1 overflow-y-auto p-2">
                <div className="space-y-1">
                  {filteredPatients?.map((patient) => (
                    <button
                      key={patient?.id}
                      onClick={() => handlePatientClick(patient)}
                      className="w-full p-3 rounded-lg text-left hover:bg-muted transition-colors duration-150"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-foreground truncate">{patient?.name}</div>
                          <div className="text-xs text-muted-foreground mt-1">
                            Last visit: {patient?.lastVisit}
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2 ml-2">
                          <div className={`
                            w-2 h-2 rounded-full
                            ${patient?.status === 'active' ? 'bg-success' : 'bg-muted-foreground'}
                          `} />
                          <div className={`
                            px-2 py-1 rounded text-xs font-medium
                            ${getRiskLevelBg(patient?.riskLevel)} ${getRiskLevelColor(patient?.riskLevel)}
                          `}>
                            {patient?.riskLevel}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className={className}>
      <DesktopSidebar />
      <MobileSidebar />
    </div>
  );
};

export default ProviderSidebar;